/**
 * One build step as a light line in plain words ("Created the home page") — no
 * tool names, paths or commands on the row. Hover for what the step is for and
 * the technical detail; click for the full input/output.
 */
import { useState } from 'react';
import type { ToolPart } from '~/components/chat/message-parts';
import WithTooltip from '~/components/ui/Tooltip';
import { plainStep } from './plain-steps';
import { ToolDetail } from './ToolDetail';

export function StepTooltip({ explain, tech }: { explain: string; tech?: string }) {
  return (
    <div className="flex flex-col gap-1 text-[12.5px] leading-snug">
      <span className="text-primary">{explain}</span>
      {tech && <span className="font-geist-mono text-[11px] text-muted break-all">{tech}</span>}
    </div>
  );
}

export function ToolLine({ part, isStreaming = false }: { part: ToolPart; isStreaming?: boolean }) {
  const [open, setOpen] = useState(false);
  const isComplete = part.state === 'output-available';
  const isError = part.state === 'output-error';
  // A tool with no terminal state after the run ended is stalled, not running —
  // show it stopped rather than spinning forever.
  const isRunning = !isComplete && !isError && isStreaming;
  const isStalled = !isComplete && !isError && !isStreaming;
  const step = plainStep(part, !isRunning);

  return (
    <div className="my-px">
      <WithTooltip
        tooltip={
          <StepTooltip
            explain={isStalled ? 'Stopped — the build ended before this step finished.' : step.explain}
            tech={step.tech}
          />
        }
        position="top"
        delay={300}
        maxWidth={320}
      >
        <div
          className="group/tl flex items-center gap-2 py-[3px] px-1.5 -mx-1.5 rounded-md cursor-pointer select-none transition-colors hover:bg-white/[0.025]"
          onClick={() => setOpen(!open)}
        >
          <span
            className={`${isStalled ? 'i-ph:pause' : step.icon} text-[13px] flex-shrink-0 ${
              isRunning ? 'text-accent' : 'text-faint'
            }`}
          />
          <span className={`text-[13.5px] truncate min-w-0 ${isRunning ? 'text-secondary ui-pulsing' : 'text-muted'}`}>
            {step.text}
          </span>
          {isError && <span className="text-[12px] text-faint flex-shrink-0">· retried</span>}
        </div>
      </WithTooltip>
      {open && (
        <div
          className="ml-5 my-1.5 rounded-lg border border-hairline px-3.5 py-3 flex flex-col gap-3"
          onClick={(e) => e.stopPropagation()}
        >
          <ToolDetail part={part} />
        </div>
      )}
    </div>
  );
}
